/**
 * ConfirmDialog — an in-app replacement for window.confirm().
 *
 * The native dialog is fine functionally and wrong everywhere else: it renders the browser's
 * chrome and the bare origin ("stage.autoaccesssolutions.org says"), ignores the app's styling,
 * and cannot show anything richer than one run of plain text. On a billing screen, where the
 * dialog is often the last thing between a click and money moving, that reads as unfinished.
 *
 * Deliberately not a general-purpose modal: it does one thing, so the callers stay a single
 * piece of state rather than a hand-rolled overlay each time.
 *
 * Styled to match the admin surfaces (light glass card, #c41e3a accent) rather than the darker
 * report modals, since that's where confirmations live.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';

export interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  /** Body copy. A node rather than a string so callers can bold the part that matters. */
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' for irreversible or outward-facing actions (sending, deleting). */
  tone?: 'default' | 'danger';
  /** Shows a spinner on the confirm button and locks the dialog while the action runs. */
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  isBusy = false,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Focus the confirm button on open, so the dialog is operable from the keyboard the way the
  // native one was — Enter to accept, Escape to dismiss.
  useEffect(() => {
    if (isOpen) confirmRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isBusy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isBusy, onCancel]);

  if (!isOpen) return null;

  const confirmClasses = tone === 'danger'
    ? 'bg-[#c41e3a] hover:bg-[#a51830] text-white'
    : 'bg-[#0f172a] hover:bg-[#1e293b] text-white';

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 print:hidden"
      onClick={() => !isBusy && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
    >
      <div
        className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full border border-black/[0.08] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              tone === 'danger' ? 'bg-[#c41e3a]/10 text-[#c41e3a]' : 'bg-black/[0.05] text-[#475569]'
            }`}>
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 id="confirm-dialog-title" className="text-lg font-bold text-[#0f172a]">{title}</h3>
              <div className="mt-2 text-sm text-[#475569] leading-relaxed">{message}</div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-black/[0.08] bg-black/[0.02] rounded-b-2xl">
          <button
            onClick={onCancel}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-semibold rounded-lg text-[#475569] hover:bg-black/[0.05] transition-colors disabled:opacity-40"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={isBusy}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors disabled:opacity-40 ${confirmClasses}`}
          >
            {isBusy && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;

/** What a caller passes to `confirm()` — the dialog's copy plus the action to run on accept. */
export interface ConfirmRequest {
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** Awaited, with the dialog held open and spinning, so a slow action can't be double-fired. */
  action: () => void | Promise<void>;
}

/**
 * useConfirm — the ergonomics window.confirm had, without the browser dialog.
 *
 * A page calls `confirm({...})` where it used to call `window.confirm(...)`, and spreads
 * `dialogProps` onto one <ConfirmDialog /> near its root. That keeps the swap to two lines per
 * page, which is the difference between replacing every native dialog in the app and replacing
 * the one someone happened to screenshot.
 *
 * The action runs inside the hook rather than at the call site so every caller gets the same
 * behaviour for free: the dialog stays open and disabled while the action is in flight, and
 * closes only when it settles — including when it throws, which callers report through their own
 * error state.
 */
export function useConfirm() {
  const [pending, setPending] = useState<ConfirmRequest | null>(null);
  const [busy, setBusy] = useState(false);

  const confirm = useCallback((request: ConfirmRequest) => setPending(request), []);
  const cancel = useCallback(() => setPending(null), []);

  const accept = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await pending.action();
    } finally {
      setBusy(false);
      setPending(null);
    }
  }, [pending]);

  const dialogProps: ConfirmDialogProps = {
    isOpen: pending !== null,
    title: pending?.title ?? '',
    message: pending?.message,
    confirmLabel: pending?.confirmLabel,
    cancelLabel: pending?.cancelLabel,
    tone: pending?.tone,
    isBusy: busy,
    onConfirm: accept,
    onCancel: cancel,
  };

  return { confirm, dialogProps };
}
